import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ClipboardList, Plus, FileText, ShieldAlert, Clock, CheckCircle, ChevronRight, Settings, Wrench, BarChart3, AlertTriangle, AlertOctagon, MapPin, Truck, Briefcase, List } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FormsPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [templatesRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/forms/templates`, { headers }),
        axios.get(`${API}/forms/submissions?per_page=10`, { headers })
      ]);

      setTemplates(templatesRes.data || []);
      setRecentSubmissions(submissionsRes.data.submissions || []);
    } catch (err) {
      console.error('Failed to fetch forms data:', err);
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const getIconForCategory = (category) => {
    const cat = (category || '').toLowerCase();
    switch (cat) {
      case 'safety':
        return <ShieldAlert className="h-6 w-6" />;
      case 'logistics':
        return <Truck className="h-6 w-6" />;
      case 'operations':
        return <Briefcase className="h-6 w-6" />;
      default:
        return <ClipboardList className="h-6 w-6" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="forms-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Forms</h1>
          <p className="text-muted-foreground">Fill out and manage company forms</p>
        </div>
        {isManager && (
          <div className="flex gap-2">
            <Link to="/dashboard/forms/submissions">
              <Button variant="outline">
                <FileText className="h-4 w-4 mr-2" />
                All Submissions
              </Button>
            </Link>
            <Link to="/dashboard/forms/builder">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create New Form
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Analytics Quick Links */}
      {isManager && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Safety Analytics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link to="/dashboard/forms/analytics">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1 border-emerald-500/30 hover:bg-emerald-500/10">
                  <ClipboardList className="h-5 w-5 text-emerald-400" />
                  <span className="text-xs">FLHA Analytics</span>
                </Button>
              </Link>
              <Link to="/dashboard/forms/analytics/hazard_id">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1 border-amber-500/30 hover:bg-amber-500/10">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  <span className="text-xs">Hazard ID Analytics</span>
                </Button>
              </Link>
              <Link to="/dashboard/forms/analytics/incident">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1 border-red-500/30 hover:bg-red-500/10">
                  <AlertOctagon className="h-5 w-5 text-red-400" />
                  <span className="text-xs">Incident Analytics</span>
                </Button>
              </Link>
              <Link to="/dashboard/forms/analytics/site_tour">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1 border-blue-500/30 hover:bg-blue-500/10">
                  <MapPin className="h-5 w-5 text-blue-400" />
                  <span className="text-xs">Site Tour Analytics</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Logistics Forms Section */}
      {templates.filter(t => (t.category || '').toLowerCase() === 'logistics').length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-500" />
            Logistics Forms
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.filter(t => (t.category || '').toLowerCase() === 'logistics').map((template) => (
              <Card key={template.id} className="card-hover h-full border-blue-500/20 hover:border-blue-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                      {getIconForCategory(template.category)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{template.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {template.description}
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {template.submission_count || 0} submissions
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <Link to={`/dashboard/forms/fill/${template.id}`} className="flex-1">
                          <Button className="w-full bg-blue-600 hover:bg-blue-700" size="sm">
                            Fill Out Form
                          </Button>
                        </Link>
                        {template.name === 'Scale Receipt' ? (
                          <Link to="/dashboard/scale-receipts">
                            <Button variant="outline" size="sm" className="border-blue-500/50 text-blue-400">
                              <List className="h-4 w-4" />
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/dashboard/forms/log/${template.id}`}>
                            <Button variant="outline" size="sm" className="border-blue-500/50 text-blue-400">
                              <List className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        {isManager && (
                          <Link to={`/dashboard/forms/builder/${template.id}`}>
                            <Button variant="outline" size="sm">
                              <Settings className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Operations Forms Section */}
      {templates.filter(t => (t.category || '').toLowerCase() === 'operations').length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-emerald-500" />
            Operations Forms
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.filter(t => (t.category || '').toLowerCase() === 'operations').map((template) => (
              <Card key={template.id} className="card-hover h-full border-emerald-500/20 hover:border-emerald-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-500">
                      {getIconForCategory(template.category)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{template.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {template.description}
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {template.submission_count || 0} submissions
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <Link to={`/dashboard/forms/fill/${template.id}`} className="flex-1">
                          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" size="sm">
                            Fill Out Form
                          </Button>
                        </Link>
                        {template.name === 'ClearSky Fuel Log' ? (
                          <Link to="/dashboard/clearsky-log">
                            <Button variant="outline" size="sm" className="border-emerald-500/50 text-emerald-400">
                              <List className="h-4 w-4" />
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/dashboard/forms/log/${template.id}`}>
                            <Button variant="outline" size="sm" className="border-emerald-500/50 text-emerald-400">
                              <List className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        {isManager && (
                          <Link to={`/dashboard/forms/builder/${template.id}`}>
                            <Button variant="outline" size="sm">
                              <Settings className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Safety Forms Section */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-yellow-500" />
          Safety Forms
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.filter(t => {
            const cat = (t.category || '').toLowerCase();
            return cat !== 'logistics' && cat !== 'operations';
          }).map((template) => (
            <Card key={template.id} className="card-hover h-full border-yellow-500/20 hover:border-yellow-500/50 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-yellow-500/10 text-yellow-500">
                    {getIconForCategory(template.category)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground">{template.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {template.description}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {template.submission_count || 0} submissions
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <Link to={`/dashboard/forms/fill/${template.id}`} className="flex-1">
                        <Button className="w-full bg-yellow-600 hover:bg-yellow-700" size="sm">
                          Fill Out Form
                        </Button>
                      </Link>
                      <Link to={`/dashboard/forms/log/${template.id}`}>
                        <Button variant="outline" size="sm" className="border-yellow-500/50 text-yellow-400">
                          <List className="h-4 w-4" />
                        </Button>
                      </Link>
                      {isManager && (
                        <Link to={`/dashboard/forms/builder/${template.id}`}>
                          <Button variant="outline" size="sm">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Submissions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Submissions
            </CardTitle>
            <Link to="/dashboard/forms/submissions">
              <Button variant="outline" size="sm">View All</Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentSubmissions.length > 0 ? (
            <div className="space-y-3">
              {recentSubmissions.map((sub) => (
                <Link key={sub.id} to={`/dashboard/forms/view/${sub.id}`}>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <div>
                        <p className="font-medium text-foreground">{sub.template_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {sub.submitted_by} • {formatDate(sub.submitted_at)}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No submissions yet</p>
          )}
        </CardContent>
      </Card>

      {/* Offline Status */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <ClipboardList className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="font-medium text-foreground">Offline Support</p>
              <p className="text-sm text-muted-foreground">
                Forms can be filled offline and will sync when you&apos;re back online
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
